import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { getErrorMessage } from '@/utils/errors';
import { adminCatalogApi, skillsPluginsApi } from '@/services/api';
import type { CatalogEntryCreate } from '@/types';
import type { CatalogEntryRequestAdmin } from '@/services/api';

const EMPTY_FORM: CatalogEntryCreate = {
  name: '',
  entry_type: 'skill',
  marketplace_repo: '',
  marketplace_name: '',
  description: '',
  source_url: '',
};

type RequestStatusFilter = 'all' | 'pending' | 'approved' | 'rejected';

function requestStatusColor(
  status: CatalogEntryRequestAdmin['status'],
): 'warning' | 'success' | 'error' {
  if (status === 'pending') return 'warning';
  if (status === 'approved') return 'success';
  return 'error';
}

function requestStatusLabel(status: CatalogEntryRequestAdmin['status']): string {
  if (status === 'pending') return 'Pending';
  if (status === 'approved') return 'Approved';
  return 'Rejected';
}

export default function AdminCataloguePage() {
  const queryClient = useQueryClient();

  // Catalogue tab state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<CatalogEntryCreate>(EMPTY_FORM);

  // Requests tab state
  const [activeTab, setActiveTab] = useState(0);
  const [statusFilter, setStatusFilter] = useState<RequestStatusFilter>('all');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [rejectTargetId, setRejectTargetId] = useState<string | null>(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const notify = (message: string, severity: 'success' | 'error') =>
    setSnackbar({ open: true, message, severity });

  // Catalog query
  const { data: entries } = useQuery({
    queryKey: ['admin-catalog'],
    queryFn: () => adminCatalogApi.list().then((res) => res.data),
  });

  // Admin requests query
  const { data: allRequests } = useQuery({
    queryKey: ['admin-sp-requests'],
    queryFn: () => skillsPluginsApi.getAdminRequests().then((res) => res.data),
  });

  const pendingCount = (allRequests ?? []).filter((r) => r.status === 'pending').length;

  const filteredRequests = (allRequests ?? []).filter((r) => {
    if (statusFilter === 'all') return true;
    return r.status === statusFilter;
  });

  // Mutations — catalog
  const createMutation = useMutation({
    mutationFn: (data: CatalogEntryCreate) => adminCatalogApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-catalog'] });
      notify('Catalogue entry added!', 'success');
      handleClose();
    },
    onError: (error: Error) => {
      notify(`Failed to add entry: ${getErrorMessage(error)}`, 'error');
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      adminCatalogApi.update(id, { is_active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-catalog'] });
    },
    onError: (error: Error) => {
      notify(`Failed to update entry: ${getErrorMessage(error)}`, 'error');
    },
  });

  // Mutations — requests
  const approveMutation = useMutation({
    mutationFn: (requestId: string) =>
      skillsPluginsApi.reviewRequest(requestId, { action: 'approve' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sp-requests'] });
      queryClient.invalidateQueries({ queryKey: ['admin-catalog'] });
      notify('Request approved!', 'success');
    },
    onError: (error: Error) => {
      notify(`Failed to approve request: ${getErrorMessage(error)}`, 'error');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ requestId, note }: { requestId: string; note: string }) =>
      skillsPluginsApi.reviewRequest(requestId, {
        action: 'reject',
        review_note: note || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sp-requests'] });
      notify('Request rejected.', 'success');
      handleRejectClose();
    },
    onError: (error: Error) => {
      notify(`Failed to reject request: ${getErrorMessage(error)}`, 'error');
    },
  });

  const handleClose = () => {
    setDialogOpen(false);
    setForm(EMPTY_FORM);
  };

  const openRejectDialog = (requestId: string) => {
    setRejectTargetId(requestId);
    setRejectNote('');
    setRejectDialogOpen(true);
  };

  const handleRejectClose = () => {
    setRejectDialogOpen(false);
    setRejectTargetId(null);
    setRejectNote('');
  };

  const handleConfirmReject = () => {
    if (!rejectTargetId) return;
    rejectMutation.mutate({ requestId: rejectTargetId, note: rejectNote });
  };

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={(_, newValue: number) => setActiveTab(newValue)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Catalogue" id="tab-catalogue" aria-controls="tabpanel-catalogue" />
        <Tab
          label={
            <Badge badgeContent={pendingCount} color="warning" max={99}>
              Requests
            </Badge>
          }
          id="tab-requests"
          aria-controls="tabpanel-requests"
          sx={{ pr: pendingCount > 0 ? 3 : undefined }}
        />
      </Tabs>

      {/* ---- Catalogue tab ---- */}
      {activeTab === 0 && (
        <Box role="tabpanel" id="tabpanel-catalogue" aria-labelledby="tab-catalogue">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              Skills &amp; Plugins Catalogue
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              Add Entry
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Marketplace Repo</TableCell>
                  <TableCell align="right">Active</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries?.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{entry.name}</TableCell>
                    <TableCell>
                      <Chip label={entry.entry_type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{entry.marketplace_repo}</TableCell>
                    <TableCell align="right">
                      <Switch
                        aria-label={`Toggle ${entry.name} active`}
                        checked={entry.is_active}
                        onChange={(e) =>
                          toggleActiveMutation.mutate({
                            id: entry.id,
                            is_active: e.target.checked,
                          })
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ---- Requests tab ---- */}
      {activeTab === 1 && (
        <Box role="tabpanel" id="tabpanel-requests" aria-labelledby="tab-requests">
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {(['all', 'pending', 'approved', 'rejected'] as RequestStatusFilter[]).map(
              (value) => (
                <Chip
                  key={value}
                  label={value.charAt(0).toUpperCase() + value.slice(1)}
                  onClick={() => setStatusFilter(value)}
                  color={
                    statusFilter === value
                      ? value === 'pending'
                        ? 'warning'
                        : value === 'approved'
                          ? 'success'
                          : value === 'rejected'
                            ? 'error'
                            : 'primary'
                      : 'default'
                  }
                  variant={statusFilter === value ? 'filled' : 'outlined'}
                  size="small"
                />
              ),
            )}
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Team</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Repo</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRequests.map((req) => (
                  <TableRow key={req.id}>
                    <TableCell>
                      <Typography variant="body2">{req.team_name ?? req.team_id}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {req.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={req.entry_type === 'plugin' ? 'Plugin' : 'Skill'}
                        size="small"
                        color={req.entry_type === 'plugin' ? 'secondary' : 'primary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {req.marketplace_repo}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      <Tooltip title={req.description ?? ''} placement="top-start">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          sx={{ maxWidth: 220 }}
                        >
                          {req.description ?? '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={requestStatusLabel(req.status)}
                        size="small"
                        color={requestStatusColor(req.status)}
                        variant="outlined"
                      />
                      {req.review_note && (
                        <Tooltip title={req.review_note} placement="top-start">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', maxWidth: 160 }}
                            noWrap
                          >
                            {req.review_note}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {req.status === 'pending' && (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => approveMutation.mutate(req.id)}
                            disabled={
                              approveMutation.isPending || rejectMutation.isPending
                            }
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => openRejectDialog(req.id)}
                            disabled={
                              approveMutation.isPending || rejectMutation.isPending
                            }
                          >
                            Reject
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {filteredRequests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" py={2}>
                        No requests found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Add catalogue entry dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add Catalogue Entry</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={form.entry_type}
              onChange={(e) =>
                setForm({ ...form, entry_type: e.target.value as 'skill' | 'plugin' })
              }
            >
              <MenuItem value="skill">Skill</MenuItem>
              <MenuItem value="plugin">Plugin</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Marketplace Repo (owner/repo)"
            value={form.marketplace_repo}
            onChange={(e) => setForm({ ...form, marketplace_repo: e.target.value })}
            placeholder="obra/superpowers"
            fullWidth
          />
          <TextField
            label="Marketplace Name"
            value={form.marketplace_name}
            onChange={(e) => setForm({ ...form, marketplace_name: e.target.value })}
            placeholder="superpowers-dev"
            fullWidth
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            multiline
            rows={2}
            fullWidth
          />
          <TextField
            label="Source URL"
            value={form.source_url}
            onChange={(e) => setForm({ ...form, source_url: e.target.value })}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => createMutation.mutate(form)}
            disabled={
              !form.name ||
              !form.marketplace_repo ||
              !form.marketplace_name ||
              createMutation.isPending
            }
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject reason dialog */}
      <Dialog open={rejectDialogOpen} onClose={handleRejectClose} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Request</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <TextField
            label="Rejection reason (optional)"
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            multiline
            rows={3}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectClose}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmReject}
            disabled={rejectMutation.isPending}
          >
            Confirm Reject
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
